// Weekly dashboard (GRI-957): pure helpers for the overview. No React here, so they are easy to test.
import { DashboardWeek } from '../models/dashboard';

// A score below this is critical and shows in red, as in the weekly email.
export const CRITICAL_SCORE = 75;
// Store score chart: one color per location, in the order of the legend.
export const LOCATION_COLORS = [
  '#325882',
  '#B75E32',
  '#00848D',
  '#8D5ED2',
  '#D43099',
  '#73777F',
];

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const DAY_MS = 24 * 60 * 60 * 1000;

const parse = (isoDate: string) => new Date(`${isoDate}T00:00:00Z`);

// "Oct 19"
export const shortDate = (isoDate: string) => {
  const d = parse(isoDate);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
};

// "Oct 26 – Nov 1, 2026"
export const weekRange = (week: Pick<DashboardWeek, 'weekStart' | 'weekEnd'>) =>
  `${shortDate(week.weekStart)} – ${shortDate(week.weekEnd)}, ${parse(
    week.weekEnd
  ).getUTCFullYear()}`;

// "Last week", "3 weeks ago", relative to the week that contains `today`.
export const weeksAgoLabel = (weekStart: string, today: Date = new Date()) => {
  const todayUtc = Date.UTC(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const ago = Math.floor(
    (todayUtc - parse(weekStart).getTime()) / (7 * DAY_MS)
  );
  if (ago <= 0) return 'This week';
  if (ago === 1) return 'Last week';
  return `${ago} weeks ago`;
};

// Badge color of a location score, as in the design prototype.
export const scoreColor = (score: number) =>
  score >= 86 ? '#006C50' : score >= CRITICAL_SCORE ? '#6F5D00' : '#BA1A1A';

export type ScoreSeries = {
  locationId: string;
  name: string;
  color: string;
  values: (number | null)[];
};

// One line per location for the store score chart, oldest week first. Locations that never
// reported in these weeks are left out; a week a location was offline is a gap (null).
export const scoreSeries = (
  weeksNewestFirst: DashboardWeek[]
): ScoreSeries[] => {
  const weeks = [...weeksNewestFirst].reverse();
  const names = new Map<string, string>();
  weeks.forEach((w) =>
    w.locations.forEach((l) => {
      if (!l.offline) names.set(l.locationId, l.name || l.locationId);
    })
  );
  return [...names.entries()]
    .sort((a, b) => a[1].localeCompare(b[1]))
    .map(([locationId, name], i) => ({
      locationId,
      name,
      color: LOCATION_COLORS[i % LOCATION_COLORS.length],
      values: weeks.map((w) => {
        const loc = w.locations.find((l) => l.locationId === locationId);
        return loc && !loc.offline ? loc.score : null;
      }),
    }));
};

// Location report (v1 weekly report style): colors of the score metrics, one per audit.
export const METRIC_LINE = [
  '#9e67ab',
  '#5a9bd4',
  '#7ac36a',
  '#faa75a',
  '#f15a60',
];
export const METRIC_FILL = [
  'rgba(157, 0, 255, 0.2)',
  'rgba(0, 123, 255, 0.2)',
  'rgba(16, 185, 12, 0.2)',
  'rgba(255, 165, 0, 0.2)',
  'rgba(241, 90, 96, 0.2)',
];

export type Metric = {
  auditId: string;
  name: string;
  category: string;
  maxPoints: number;
};
export type LocationWeekPoints = {
  weekStart: string;
  points: Record<string, number>;
  score: number;
};

// A location's history for its report, as in the v1 weekly report: the selected week and the
// weeks before it in which the location reported, newest first, at most `max`.
// Metrics are the audits seen in those weeks, in the selected week's order first.
export const locationHistory = (
  weeksNewestFirst: DashboardWeek[],
  selected: number,
  locationId: string,
  max = 8
): { metrics: Metric[]; history: LocationWeekPoints[] } => {
  const reported = weeksNewestFirst
    .slice(selected)
    .map((w) => ({
      w,
      loc: w.locations.find((l) => l.locationId === locationId),
    }))
    .filter(({ loc }) => loc && !loc.offline && loc.score !== null)
    .slice(0, max);
  const metrics = new Map<string, Metric>();
  reported.forEach(({ loc }) =>
    loc!.audits.forEach((a) => {
      if (!metrics.has(a.auditId)) {
        metrics.set(a.auditId, {
          auditId: a.auditId,
          name: a.name || a.auditId,
          category: a.category || '',
          maxPoints: a.maxPoints,
        });
      }
    })
  );
  return {
    metrics: [...metrics.values()],
    history: reported.map(({ w, loc }) => ({
      weekStart: w.weekStart,
      score: loc!.score as number,
      points: Object.fromEntries(loc!.audits.map((a) => [a.auditId, a.points])),
    })),
  };
};
// The only pages a dashboard user can open; anything else (videos, orders, devices, "/") sends
// them to /dashboard.
export const isDashboardUserPath = (pathname: string) =>
  pathname === '/dashboard' ||
  pathname.startsWith('/dashboard/') ||
  pathname === '/login' ||
  pathname === '/callback';
